// src/components/PackageSelectDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Stack,
  Alert,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { fetchPackagesByProduct } from "../../../services/saleng";
import CloseIcon from "@mui/icons-material/Close";

import moment from "moment";
import "moment/locale/th";

export default function PackageSelectDialog({
  open,
  onClose,
  productId,
  onConfirm,
  columns,
  initialSelectionId,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [query, setQuery] = useState("");

  // single select (เก็บเป็น array แต่บังคับให้เหลือ 1 เสมอ)
  const [selectionModel, setSelectionModel] = useState(
    initialSelectionId != null ? [initialSelectionId] : []
  );

  // โหลดข้อมูลเมื่อ open หรือ productId เปลี่ยน
  useEffect(() => {
    if (!open) return;
    if (!productId) {
      setRows([]);
      setSelectionModel([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErrMsg("");
        const data = await fetchPackagesByProduct(productId);
        if (!cancelled) {
          setRows(Array.isArray(data) ? data : []);
          setSelectionModel(
            initialSelectionId != null ? [initialSelectionId] : []
          );
          setQuery("");
        }
      } catch (err) {
        console.error("fetchPackagesByProduct error:", err);
        if (!cancelled) {
          setErrMsg(
            err?.response?.data?.message ||
              err?.message ||
              "ไม่สามารถดึงข้อมูลแพ็คได้"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, productId, initialSelectionId]);

  // filter บน client
  const filteredRows = useMemo(() => {
    if (!query) return rows;
    const q = String(query).toLowerCase();
    return rows.filter((r) =>
      [
        r.id,
        r.product?.title,
        r.type,
        r.quantity?.toString?.(),
        r.updatedAt,
        r.createdAt,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query]);

  // ---- helpers: รองรับ v5/v6 สำหรับ valueGetter ----
  const getFromArgs = (args) => {
    // v6: (value, row, colDef)
    if (args.length >= 2) {
      const row = args[1];
      const value = args[0];
      return { row, value };
    }
    // v5: (params)
    const params = args[0] || {};
    return { row: params.row, value: params.value };
  };

  const renderThaiBuddhistCell = (params) => {
    const raw = params?.value ?? params?.row?.[params.field];
    if (!raw) return "-";
    const d = moment(raw).locale("th");
    if (!d.isValid()) return "-";
    const buddhistYear = d.year() + 543;
    return `${d.format("DD MMMM ")}${buddhistYear} เวลา ${d.format("HH:mm")}`;
  };

  const defaultColumns = [
    {
      field: "title",
      headerName: "สินค้า",
      flex: 1,
      minWidth: 180,
      valueGetter: (...args) => {
        const { row } = getFromArgs(args);
        return row?.product?.title ?? row?.productName ?? "-";
      },
    },
    { field: "type", headerName: "ประเภทแพ็ค", flex: 0.7, minWidth: 120 },
    {
      field: "quantity",
      headerName: "น้ำหนัก (กก.)",
      width: 180,
      valueFormatter: (arg) => {
        const val = typeof arg === "object" ? arg?.value : arg;
        return val ?? "-";
      },
    },
    {
      field: "createdAt",
      headerName: "สร้างเมื่อ",
      flex: 1,
      minWidth: 200,
      // ให้ grid ถือค่า ISO จริงไว้สำหรับ sort/filter
      valueGetter: (...args) => {
        const { row, value } = getFromArgs(args);
        return value ?? row?.createdAt ?? null;
      },
      renderCell: renderThaiBuddhistCell,
      sortComparator: (a, b) => new Date(a) - new Date(b),
    },
  ];

  const finalColumns = columns?.length ? columns : defaultColumns;

  const canConfirm = selectionModel.length === 1;
  const selectedRow = canConfirm
    ? filteredRows.find((r) => String(r.id) === String(selectionModel[0]))
    : null;

  const handleConfirm = () => {
    if (selectedRow) onConfirm?.(selectedRow);
  };

  // props เฉพาะเวอร์ชัน grid (ใส่ทั้งคู่ ปล่อยตัวที่ lib ไม่รู้จักทิ้งไป)
  const selectionProps = {
    // v6
    rowSelectionModel: selectionModel,
    onRowSelectionModelChange: (newModel) =>
      setSelectionModel(newModel.slice(-1)),
    disableMultipleRowSelection: true, // v6 เท่านั้น

    // v5 (deprecated ใน v6 แต่ไม่พัง)
    selectionModel: selectionModel,
    onSelectionModelChange: (newModel) =>
      setSelectionModel([].concat(newModel).slice(-1)),
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        เลือกแพ็คสินค้า{" "}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ minWidth: 720 }}>
          <TextField
            placeholder="ค้นหา สินค้า, ประเภท, น้ำหนัก… "
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {errMsg && <Alert severity="error">{errMsg}</Alert>}

          <Box sx={{ height: 420, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={finalColumns}
              loading={loading}
              checkboxSelection
              hideFooterSelectedRowCount
              pageSizeOptions={[5, 10, 20]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
                sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] },
              }}
              // กัน click เซลล์แล้วเลือก
              disableSelectionOnClick
              disableRowSelectionOnClick
              // single select
              {...selectionProps}
              onRowDoubleClick={(params) => {
                const id = params?.id;
                if (id != null) {
                  setSelectionModel([id]);
                  const row = filteredRows.find(
                    (r) => String(r.id) === String(id)
                  );
                  if (row) onConfirm?.(row);
                }
              }}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} variant="outlined">
          ยกเลิก
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
}
